import axios from 'axios';

import { getEnvVariable } from '~/dev/utils/getEnvVariable';

import { acquireAccessToken } from '../spe';

/**
 * Permission granted on the Microsoft Graph API
 */
export type GraphApiPermission = {
  id: string;
  appRoleId: string;
  resourceDisplayName: string;
  isGranted: boolean;
};

/**
 * Response type for Graph API permissions
 */
export type GraphApiPermissionsResponse = {
  permissions: GraphApiPermission[];
  isFullyConfigured: boolean;
};

/**
 * App role assignment from MS Graph API
 * @see https://learn.microsoft.com/en-us/graph/api/serviceprincipal-list-approleassignments
 */
type AppRoleAssignment = {
  id: string;
  appRoleId: string;
  resourceDisplayName: string;
  resourceId: string;
  principalId: string;
};

type ServicePrincipalResponse = {
  value: Array<{ id: string }>;
};

type AppRoleAssignmentsResponse = {
  value: AppRoleAssignment[];
};

/**
 * Required app role IDs for SharePoint Embedded integration.
 * These are the GUIDs for the Microsoft Graph application permissions.
 */
const REQUIRED_APP_ROLES = [
  {
    id: '40dc41bc-0f7e-42ff-89bd-d9516947e474',
    name: 'FileStorageContainer.Selected',
  },
];

const mapResponse = (
  assignments: AppRoleAssignment[],
): GraphApiPermissionsResponse => {
  const grantedRoleIds = new Set(assignments.map((a) => a.appRoleId));

  const permissions: GraphApiPermission[] = REQUIRED_APP_ROLES.map((role) => ({
    id: role.id,
    appRoleId: role.id,
    resourceDisplayName: role.name,
    isGranted: grantedRoleIds.has(role.id),
  }));

  const isFullyConfigured = REQUIRED_APP_ROLES.every((role) =>
    grantedRoleIds.has(role.id),
  );

  return { permissions, isFullyConfigured };
};

/**
 * Gets the current Graph API permissions status for the SharePoint Embedded integration.
 * This checks if the required admin consent (application permissions) have been granted.
 *
 * @see https://learn.microsoft.com/en-us/graph/api/serviceprincipal-list-approleassignments
 */
export async function getGraphApiPermissions(): Promise<GraphApiPermissionsResponse> {
  const msIdentity = await acquireAccessToken();
  const clientId = getEnvVariable('EVISORT_SPE_CLIENT_ID');

  const headers = {
    Authorization: `Bearer ${msIdentity.accessToken}`,
  };

  // Step 1: Get the service principal for our app
  const spResponse = await axios.get<ServicePrincipalResponse>(
    `https://graph.microsoft.com/v1.0/servicePrincipals`,
    {
      params: {
        $filter: `appId eq '${clientId}'`,
      },
      headers,
    },
  );

  const servicePrincipals = spResponse.data.value;
  if (servicePrincipals.length === 0) {
    // App not registered as service principal in this tenant yet
    return { permissions: [], isFullyConfigured: false };
  }

  const servicePrincipalId = servicePrincipals[0].id;

  // Step 2: Get app role assignments for this service principal
  const assignmentsResponse = await axios.get<AppRoleAssignmentsResponse>(
    `https://graph.microsoft.com/v1.0/servicePrincipals/${servicePrincipalId}/appRoleAssignments`,
    { headers },
  );

  return mapResponse(assignmentsResponse.data.value);
}
